/**
 * Insights contracts (punch 6.1b; docs/11 F-3).
 *
 * An insight is a CONDITION the server found, not a task somebody filed. Three
 * of them, and only three — Matt's approved list:
 *
 *   1. An active tenancy with no active lease.
 *   2. A document filed against nothing.
 *   3. An active property note explicitly flagged as an alert.
 *
 * The vocabulary that matters most here is `occurrenceId` versus `sourceKey`.
 * The SOURCE KEY is permanent: one logical condition about one record, for the
 * life of that record. The OCCURRENCE is this particular episode of it. A
 * tenancy that loses its lease, gets a new one filed, and loses that one too
 * has one source key and two occurrences — and an acknowledgement of the first
 * must not silence the second. Every acknowledgement and snooze therefore binds
 * to the occurrence, never to the source key.
 */

import { z } from 'zod';

/** The three approved sources. A fourth is a product decision, not a patch. */
export const INSIGHT_KINDS = [
  'tenancy_without_lease',
  'document_without_association',
  'property_note_alert',
] as const;
export type InsightKind = (typeof INSIGHT_KINDS)[number];

/**
 * Reader-facing names, beside the enum so no screen can word one differently
 * (punch 5.3's rule). These are what a property manager sees as the group
 * heading; the per-row sentence names the actual record.
 */
export const INSIGHT_KIND_LABEL: Record<InsightKind, string> = {
  tenancy_without_lease: 'Tenancy with no lease on file',
  document_without_association: 'Document not filed on any record',
  property_note_alert: 'Property note flagged as an alert',
};

/**
 * Why it is worth a look. Deliberately says what to DO — an insight the reader
 * cannot act on is an observation, and the lane fills up with observations.
 */
export const INSIGHT_KIND_HINT: Record<InsightKind, string> = {
  tenancy_without_lease:
    'Somebody is occupying space with no active lease recorded. File the lease, or end the tenancy if they have gone.',
  document_without_association:
    'This document is in the library but attached to nothing, so nobody will find it from a record. Open it and link it to the record it belongs on.',
  property_note_alert:
    'Someone flagged this note as needing attention. Deal with it, then clear the alert on the note.',
};

export const INSIGHT_ACTIONS = ['acknowledged', 'snoozed'] as const;
export type InsightAction = (typeof INSIGHT_ACTIONS)[number];

/** One insight as the lane renders it. */
export interface InsightRow {
  /** THIS episode. What an acknowledgement or snooze binds to. */
  readonly occurrenceId: string;
  /** The permanent logical identity of the condition. */
  readonly sourceKey: string;
  readonly kind: InsightKind;
  /**
   * How many times this condition has been raised about this record. 1 the
   * first time; 2 or more means it came back, which is worth showing — a
   * recurrence is a different kind of news from a first occurrence.
   */
  readonly generation: number;
  readonly openedAt: string;
  /** The record this is about: "Bright Smiles at Cameron Valley". */
  readonly subjectLabel: string;
  /** Where to go and fix it. A route, always — an insight with no door is a nag. */
  readonly href: string;
  /** The caller's own snooze, when one is in force but already expired. */
  readonly snoozedUntil: string | null;
}

export interface InsightsResponse {
  readonly rows: readonly InsightRow[];
  /**
   * Whether the caller may acknowledge or snooze. Reading the lane and acting
   * on it are separate: `insights.ack` is in every base role today, but a
   * custom add-on role can hold one without the other (0053), so the server
   * says rather than the screen assuming.
   */
  readonly canAck: boolean;
}

/** POST /api/v1/insights/:occurrenceId/acknowledge — no body. */
export const insightAckSchema = z.object({});

/**
 * POST /api/v1/insights/:occurrenceId/snooze.
 *
 * The CALLER names the time, because "remind me after the weekend" and "remind
 * me next quarter" are both real and a fixed interval serves neither. Bounded
 * at a year: a longer snooze is an acknowledgement that has not admitted it.
 */
export const insightSnoozeSchema = z.object({
  until: z.iso.datetime(),
});

/** The offered snooze durations, in days. The UI renders these; the API takes a date. */
export const INSIGHT_SNOOZE_OPTIONS: readonly { readonly days: number; readonly label: string }[] =
  [
    { days: 1, label: 'Tomorrow' },
    { days: 7, label: 'Next week' },
    { days: 30, label: 'Next month' },
  ];
